import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const ACCOUNT_TYPE_TABS = [
  { id: '',                  label: 'All' },
  { id: 'family',            label: 'Families' },
  { id: 'regional_partner',  label: 'Regional Partners' },
  { id: 'memorial_partner',  label: 'Memorial Partners' },
];

const ACCOUNT_TYPE_PILL = {
  family:           'bg-sky-50 text-sky-700 border-sky-200',
  regional_partner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  memorial_partner: 'bg-amber-50 text-amber-700 border-amber-200',
};

function shortDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export default function AdminAccounts() {
  const [q, setQ] = useState('');
  const [accountType, setAccountType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [partnerTypeFilter, setPartnerTypeFilter] = useState('');
  const [stripeStatusFilter, setStripeStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compOpen, setCompOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (accountType) params.set('account_type', accountType);
      if (statusFilter) params.set('status', statusFilter);
      if (planFilter) params.set('plan', planFilter);
      if (partnerTypeFilter) params.set('partner_type', partnerTypeFilter);
      if (stripeStatusFilter) params.set('stripe_connect_status', stripeStatusFilter);
      if (paymentStatusFilter) params.set('payment_status', paymentStatusFilter);
      const { data } = await api.get(`/admin/accounts?${params.toString()}`);
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, accountType, statusFilter, planFilter, partnerTypeFilter, stripeStatusFilter, paymentStatusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-accounts-title">
              Accounts
            </h1>
            <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
              Search every family and partner account. Every account-detail view is audit-logged.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCompOpen(true)}
            className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition"
            data-testid="admin-accounts-comp-memorial-btn"
          >
            + Complimentary memorial
          </button>
        </header>

        <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-5 mb-6 space-y-3">
          <input
            type="text"
            placeholder="Search by name, email, or business name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full text-base bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-2 py-2"
            data-testid="admin-accounts-search"
          />
          <div className="flex flex-wrap gap-2">
            {ACCOUNT_TYPE_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setAccountType(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs border ${
                  accountType === t.id
                    ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                    : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)]'
                }`}
                data-testid={`admin-accounts-type-${t.id || 'all'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
            <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={[
              ['', 'Any status'],
              ['active', 'Active'],
              ['pending', 'Pending'],
              ['approved', 'Approved'],
              ['rejected', 'Rejected'],
              ['disabled', 'Disabled'],
            ]} testId="admin-accounts-status" />
            <FilterSelect label="Plan" value={planFilter} onChange={setPlanFilter} options={[
              ['', 'Any plan'], ['memorial', 'Memorial'], ['memorial_plus', 'Memorial Plus'], ['legacy', 'Legacy'],
            ]} testId="admin-accounts-plan" />
            <FilterSelect label="Partner type" value={partnerTypeFilter} onChange={setPartnerTypeFilter} options={[
              ['', 'Any'], ['individual', 'Individual'], ['business', 'Business'],
            ]} testId="admin-accounts-partner-type" />
            <FilterSelect label="Payment status" value={paymentStatusFilter} onChange={setPaymentStatusFilter} options={[
              ['', 'Any'], ['paid', 'Paid'], ['pending', 'Pending'], ['failed', 'Failed'],
            ]} testId="admin-accounts-payment" />
            <FilterSelect label="Stripe Connect" value={stripeStatusFilter} onChange={setStripeStatusFilter} options={[
              ['', 'Any'], ['active', 'Active'], ['incomplete', 'Incomplete'],
            ]} testId="admin-accounts-stripe" />
          </div>
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : accounts.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-accounts-empty">
            No accounts match these filters.
          </div>
        ) : (
          // iter72 — Responsive card list (no horizontal swipe).
          // Desktop: a 12-col grid renders all key fields inline.
          // Mobile/tablet: each card stacks fields vertically so the
          // operator never has to swipe sideways to see Status / Plan /
          // Created / Phone.
          <ul
            className="space-y-3"
            data-testid="admin-accounts-list"
          >
            {accounts.map((a) => {
              const at = a.account_type || 'family';
              const status = a.partner_status
                || a.account_status
                || (at === 'family' ? 'active' : '—');
              return (
                <li
                  key={a.user_id}
                  data-testid={`admin-accounts-row-${a.user_id}`}
                  className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] hover:bg-[hsl(var(--primary)/0.03)] transition-colors"
                >
                  <Link
                    to={`/admin/accounts/${a.user_id}`}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-x-4 gap-y-2 px-5 py-4 items-center"
                    data-testid={`admin-accounts-open-${a.user_id}`}
                  >
                    {/* Name + business */}
                    <div className="lg:col-span-3 min-w-0">
                      <div className="ml-overline lg:hidden">Account</div>
                      <div className="text-[hsl(var(--primary))] font-medium truncate">
                        {a.name || '—'}
                      </div>
                      {a.business_name && (
                        <div className="text-[10px] text-[hsl(var(--primary)/0.55)] truncate">
                          {a.business_name}
                        </div>
                      )}
                    </div>
                    {/* Email */}
                    <div className="lg:col-span-3 min-w-0">
                      <div className="ml-overline lg:hidden">Email</div>
                      <div className="text-[12px] text-[hsl(var(--primary)/0.8)] truncate">
                        {a.email || '—'}
                      </div>
                      {a.phone && (
                        <div className="text-[10px] text-[hsl(var(--primary)/0.55)] tabular-nums">
                          {a.phone}
                        </div>
                      )}
                    </div>
                    {/* Type pill */}
                    <div className="lg:col-span-2">
                      <div className="ml-overline lg:hidden">Type</div>
                      <span
                        className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full border whitespace-nowrap ${ACCOUNT_TYPE_PILL[at] || 'border-gray-200'}`}
                      >
                        {at.replace('_', ' ')}
                      </span>
                    </div>
                    {/* Status */}
                    <div className="lg:col-span-2 min-w-0">
                      <div className="ml-overline lg:hidden">Status</div>
                      <div className="text-[12px] text-[hsl(var(--primary)/0.8)] capitalize truncate">
                        {status}
                      </div>
                      {a.plan && (
                        <div className="text-[10px] text-[hsl(var(--primary)/0.55)] capitalize">
                          Plan · {a.plan}
                        </div>
                      )}
                    </div>
                    {/* Created date + arrow */}
                    <div className="lg:col-span-2 flex items-center justify-between lg:justify-end gap-3">
                      <div>
                        <div className="ml-overline lg:hidden">Created</div>
                        <div className="text-[12px] text-[hsl(var(--primary)/0.6)] tabular-nums">
                          {shortDate(a.created_at)}
                        </div>
                      </div>
                      <span
                        aria-hidden
                        className="text-[hsl(var(--accent))] text-[12px] font-semibold tracking-[0.18em] shrink-0"
                      >
                        OPEN →
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </AdminLayout>
      {compOpen && (
        <CompMemorialModal
          onClose={() => setCompOpen(false)}
          onCreated={() => { setCompOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function CompMemorialModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: '',
    date_of_birth: '',
    date_of_passing: '',
    package_id: 'memorial',
    assign_to_email: '',
    assign_to_name: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fn = form.full_name.trim();
    const email = form.assign_to_email.trim().toLowerCase();
    if (!fn) { toast.error('Full name is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Valid family email is required.'); return; }
    if (!window.confirm(`Create complimentary memorial for "${fn}" and assign it to ${email}? This bypasses Stripe.`)) return;
    setSaving(true);
    try {
      const payload = {
        full_name: fn,
        package_id: form.package_id,
        assign_to_email: email,
      };
      if (form.date_of_birth)     payload.date_of_birth = form.date_of_birth;
      if (form.date_of_passing)   payload.date_of_passing = form.date_of_passing;
      if (form.assign_to_name)    payload.assign_to_name = form.assign_to_name.trim();
      if (form.note)              payload.note = form.note.trim();
      const { data } = await api.post('/admin/memorials/complimentary', payload);
      const wasNew = data?.owner?.was_created;
      toast.success(`Memorial created · slug "${data.memorial.slug}"${wasNew ? ' · new family account provisioned.' : ''}`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-comp-memorial-modal">
      <form onSubmit={handleSubmit} className="bg-[hsl(var(--card))] rounded-2xl max-w-xl w-full p-6 my-8 border border-[hsl(var(--primary)/0.18)] shadow-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="ml-overline text-[hsl(var(--accent))]">Admin tool</p>
            <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">Complimentary memorial</h3>
            <p className="text-xs text-[hsl(var(--primary)/0.65)] mt-1 max-w-md">
              Bypasses Stripe and marks the memorial as paid. Used for press, demos, partner showcases. Recorded in the audit log.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] text-sm" data-testid="admin-comp-memorial-close">
            Close ✕
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Deceased full name *" testid="admin-comp-name">
            <input type="text" required value={form.full_name} onChange={(e) => set('full_name', e.target.value)}
                   className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                   data-testid="admin-comp-name-input" />
          </Field>
          <Field label="Package *" testid="admin-comp-package">
            <select value={form.package_id} onChange={(e) => set('package_id', e.target.value)}
                    className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                    data-testid="admin-comp-package-select">
              <option value="memorial">Memorial ($199)</option>
              <option value="memorial_plus">Memorial Plus ($299)</option>
            </select>
          </Field>
          <Field label="Date of birth" testid="admin-comp-dob">
            <input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)}
                   className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                   data-testid="admin-comp-dob-input" />
          </Field>
          <Field label="Date of passing" testid="admin-comp-dop">
            <input type="date" value={form.date_of_passing} onChange={(e) => set('date_of_passing', e.target.value)}
                   className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                   data-testid="admin-comp-dop-input" />
          </Field>
          <Field label="Assign to family email *" testid="admin-comp-email">
            <input type="email" required value={form.assign_to_email} onChange={(e) => set('assign_to_email', e.target.value)}
                   className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                   data-testid="admin-comp-email-input" />
          </Field>
          <Field label="Family member name" testid="admin-comp-fam-name">
            <input type="text" value={form.assign_to_name} onChange={(e) => set('assign_to_name', e.target.value)}
                   className="w-full text-sm bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-1.5"
                   data-testid="admin-comp-fam-name-input" />
          </Field>
        </div>

        <label className="block mt-4">
          <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium mb-1">Internal note (why is this free?)</div>
          <textarea value={form.note} onChange={(e) => set('note', e.target.value)} rows={3}
                    className="w-full text-sm bg-transparent border border-[hsl(var(--primary)/0.18)] rounded-md focus:border-[hsl(var(--accent))] focus:outline-none px-2 py-1.5"
                    data-testid="admin-comp-note-input" />
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose}
                  className="px-5 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-sm"
                  data-testid="admin-comp-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving}
                  className="px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
                  data-testid="admin-comp-submit">
            {saving ? 'Creating…' : 'Create memorial'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="uppercase tracking-[0.18em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-[hsl(var(--primary)/0.18)] rounded px-2 py-1.5 text-[hsl(var(--primary))]"
        data-testid={testId}
      >
        {options.map(([val, lab]) => (<option key={val} value={val}>{lab}</option>))}
      </select>
    </label>
  );
}
